import { Component, OnInit, NgZone, OnDestroy, Signal } from '@angular/core';
import { SessionService } from '../../services/authentication/session.service';
import { UserService } from '../../services/user/user.service';
import { User } from '../../../model/user.model';
import { environment } from 'src/environments/environment';
import { NavigationStart, Scroll, Router } from '@angular/router';
import { UserFormComponent } from '../../../admin/user-form/user-form.component';
import { FormBuilder } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, concatMap, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LocalizationService } from '../../services/localization/localization.service';
import { WebsocketService } from '../../services/websocket/websocket.service';
import { WebSocketMessageType } from 'src/app/model/websocket.model';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit, OnDestroy {
  user: Signal<User | null>;
  isAdmin: boolean;
  isContributor: boolean;
  isReferencer: boolean;
  isSuperAdmin: boolean;
  BASE_API = environment.apiUrl;
  loading = false;
  getInfos = true;
  refreshedOnce = false;

  constructor(
    private readonly sessionService: SessionService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly ngZone: NgZone,
    private readonly http: HttpClient,
    private readonly localizationService: LocalizationService,
    private readonly webSocketService: WebsocketService,
  ) {}

  ngOnInit() {
    this.getInfos = true;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && event.url === '/account/logout') {
        this.getInfos = false;
      } else if (this.getInfos && !this.refreshedOnce && event instanceof Scroll) {
        this.getMyInformations();
        this.initializeUserRoles();
        this.refreshedOnce = true;
      }
    });
    this.refreshedOnce = false;
    this.user = this.sessionService.user;
    this.initializeUserRoles();
    this.webSocketService.subscribe({
      next: (msg) => {
        const message = JSON.parse(msg) as WebSocketMessageType;
        if (message.type === 'user' && message.properties.user.username === this.user().username) {
          switch (message.properties.context) {
            case 'delete':
              this.router.navigate(['/account/logout']);
              break;
            case 'update':
              this.sessionService.user = message.properties.user;
              break;
            case 'create':
            default:
              console.error(
                'websocket say that your account has been create while you were connected, this should not appear in theory',
              );
              break;
          }
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.webSocketService.closeConnection();
  }

  edit(u: User) {
    const formUser = this.formBuilder.group({
      username: u.username,
      name: u.name,
      firstname: u.firstname,
      picture: u.picture,
      deletePicture: false,
      email: u.email,
      roles: [u.roles],
      lang: this.localizationService.getLang(),
      enabled: true,
    });
    const modalUser = this.modalService.open(UserFormComponent);
    //enable email, set email and disable email
    modalUser.componentInstance.formUser = formUser;
    modalUser.componentInstance.action = 'SELF_EDIT';
    modalUser.componentInstance.userEdit = true;
    modalUser.componentInstance.hasApic = u.picture !== '';
    modalUser.result.then(
      (data: any) => {
        this.loading = true;
        this.editUser(data.user, data.pictureData, data.pictureDataBase64, data.deletePicture);
      },
      () => {},
    );
  }

  editUser(
    user: User,
    pictureData: ArrayBuffer = null,
    pictureDataBase64: string = null,
    deletePicture: boolean = false,
  ) {
    this.editMyself(user, pictureData, deletePicture).subscribe({
      next: (userData) => {
        if (deletePicture === false && pictureDataBase64 !== null) {
          userData.picture = pictureDataBase64;
        }
        userData.email = this.user().email;
        this.sessionService.user = userData;
        this.localizationService.loadFromStorage();
        this.loading = false;
      },
      error: (err) => {
        this.ngZone.run(() => console.error(err));
      },
    });
  }

  private editMyself(
    user: User,
    pictureData: ArrayBuffer = null,
    deletePicture: boolean = false,
  ): Observable<User> {
    let body: any = user;
    body.requestPictureUrl = Boolean(pictureData);
    body.deletePicture = deletePicture;

    const o1: Observable<any> = this.http.put<any>(
      `${this.BASE_API}/api/admin/user/` + encodeURIComponent(user.username),
      body,
    );
    const o2: Observable<any> = pictureData
      ? o1.pipe(
          concatMap((res) => {
            if (res.uploadUrl) {
              return this.http
                .put<any>(res.uploadUrl, pictureData, {
                  headers: { 'Content-Type': 'image/*' },
                })
                .pipe(map(() => res));
            } else return res;
          }),
        )
      : o1;
    const o3 = o2.pipe(
      concatMap((userUpdated) => this.getMyInformations().pipe(map((rep) => rep))),
    );
    return o3.pipe(map((linkToPic) => linkToPic));
  }

  private initializeUserRoles(): void {
    this.isAdmin = this.userService.isAdmin();
    this.isContributor = this.userService.isContributor();
    this.isReferencer = this.userService.isReferencer();
    this.isSuperAdmin = this.userService.isSuperAdmin();
  }

  private getMyInformations(): Observable<User> {
    const user = this.http.get<User>(`${this.BASE_API}/user/me/`);
    user.subscribe({
      next: (u: User) => {
        if (JSON.stringify(this.sessionService.user) !== JSON.stringify(u)) {
          this.sessionService.user = u;
          this.user = this.sessionService.user;
          this.initializeUserRoles();
        }
      },
      error: (err) => {
        console.error('Error fetching user information', err);
        // Handle error, maybe redirect to login or show a message
      },
    });
    return user;
  }

  disconnect() {
    this.getInfos = false;
    this.router.navigate(['/account/logout']);
  }
}
